import { useEffect, useRef, useState } from "react";
import { AlertTriangle, ArrowUp, CalendarCheck, Stethoscope } from "lucide-react";
import { API } from "@/lib/api";
import { Reveal, Eyebrow } from "./Reveal";

const SUGGESTIONS = ["I have a burning feeling when I pee", "How soon after exposure should I test?", "What's PEP and when do I need it?"];
const DISCLAIMER = "Not medical advice. The Sweet Results AI Nurse shares general education only and can't diagnose. Always see a licensed provider and schedule your STD test.";

const Bubble = ({ m }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[85%] rounded-3xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#FF2A8D] text-white rounded-br-md" : "bg-white/[0.06] text-slate-100 rounded-bl-md"}`}>
      {m.text || <span className="inline-flex gap-1"><span className="w-1.5 h-1.5 rounded-full bg-slate-300 animate-bounce" /><span className="w-1.5 h-1.5 rounded-full bg-slate-300 animate-bounce [animation-delay:.15s]" /><span className="w-1.5 h-1.5 rounded-full bg-slate-300 animate-bounce [animation-delay:.3s]" /></span>}
    </div>
  </div>
);

const useNurse = () => {
  const [msgs, setMsgs] = useState([{ role: "assistant", text: "Hey, I'm your Sweet Results AI Nurse. Tell me what you're feeling or ask anything about sexual health — no judgement here." }]);
  const [busy, setBusy] = useState(false);
  const sid = useRef(sessionStorage.getItem("sr_nurse_sid"));

  const send = async (text) => {
    if (!text.trim() || busy) return;
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    const append = (t) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], text: c[c.length - 1].text + t }; return c; });
    try {
      const res = await fetch(`${API}/nurse/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message: text, session_id: sid.current }) });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const d = p.replace(/^data: /, "");
          if (d === "[DONE]") continue;
          const j = JSON.parse(d);
          if (j.session_id) { sid.current = j.session_id; sessionStorage.setItem("sr_nurse_sid", j.session_id); }
          if (j.t) append(j.t);
          if (j.error) append(j.error);
        }
      }
    } catch {
      append("I couldn't connect just now. Please book a test directly and try me again shortly.");
    }
    setBusy(false);
  };
  return { msgs, busy, send };
};

export const Nurse = ({ onSchedule }) => {
  const { msgs, busy, send } = useNurse();
  const [input, setInput] = useState("");
  const box = useRef(null);
  useEffect(() => { if (box.current) box.current.scrollTop = box.current.scrollHeight; }, [msgs]);

  const submit = (e) => { e.preventDefault(); send(input); setInput(""); };

  return (
    <section id="nurse" className="relative py-24 lg:py-36 bg-[#0E1326]/60 border-y border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-32">
          <Reveal><Eyebrow color="text-[#5B95D6]">AI Nurse · live</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1]">
              Something feels off?<br /><span className="text-duo">Ask, right now.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-slate-300 text-base sm:text-lg leading-relaxed max-w-md">
              Describe your symptoms in your own words and get real-time, judgement-free guidance — then get booked in for a proper test.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 relative rounded-3xl overflow-hidden aspect-[16/10] hidden sm:block lg:block">
              <img src="/img/lab.jpg" alt="Clinical lab" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#FF2A8D]/30 to-[#125AA0]/20 mix-blend-color" />
            </div>
          </Reveal>
        </div>

        <Reveal className="lg:col-span-7" delay={0.1}>
          <div data-testid="ai-nurse-chat" className="glass rounded-[32px] overflow-hidden flex flex-col h-[620px] max-h-[80svh]">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
              <span className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF2A8D] to-[#125AA0] grid place-items-center"><Stethoscope size={18} /></span>
              <div className="flex-1">
                <p className="font-display font-bold">Sweet Results AI Nurse</p>
                <p className="text-xs text-[#10B981] flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" /> Online</p>
              </div>
              <button data-testid="ai-nurse-schedule-cta" onClick={onSchedule}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#FF2A8D] hover:bg-[#FF54A5] px-3.5 py-2 text-xs font-semibold transition-colors">
                <CalendarCheck size={14} /> Book test
              </button>
            </div>
            <div data-testid="ai-nurse-disclaimer-banner" className="flex gap-2.5 px-5 py-3 bg-[#FFB800]/10 border-b border-[#FFB800]/20 text-[12px] text-[#FFD466] leading-snug">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" /> {DISCLAIMER}
            </div>
            <div ref={box} data-lenis-prevent data-testid="ai-nurse-messages" className="flex-1 overflow-y-auto p-5 space-y-3">
              {msgs.map((m, i) => <Bubble key={i} m={m} />)}
            </div>
            {msgs.length < 3 && (
              <div className="px-5 pb-3 flex flex-wrap gap-2">
                {SUGGESTIONS.map((s, i) => (
                  <button key={s} data-testid={`ai-nurse-suggestion-${i}`} onClick={() => send(s)}
                    className="text-xs rounded-full border border-white/15 px-3 py-1.5 text-slate-300 hover:border-[#125AA0] hover:text-white transition-colors">{s}</button>
                ))}
              </div>
            )}
            <form onSubmit={submit} className="flex gap-2 p-3 border-t border-white/10">
              <input data-testid="ai-nurse-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Describe what you're experiencing…"
                className="flex-1 min-w-0 rounded-full bg-white/5 border border-white/10 px-5 py-3 text-sm focus:outline-none focus:border-[#FF2A8D]" />
              <button data-testid="ai-nurse-send-btn" disabled={busy || !input.trim()} aria-label="Send"
                className="w-12 h-12 shrink-0 rounded-full bg-[#125AA0] text-white grid place-items-center disabled:opacity-40 hover:scale-105 transition-transform">
                <ArrowUp size={20} />
              </button>
            </form>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
